'use client';

import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { FiChevronDown } from 'react-icons/fi';

import { Box, Menu, UnstyledButton } from '@highlight-cp/core-ui';

import classes from './top-nav-link.module.css';

interface TopNavLinkProps {
  label: string;
  href: string;
  isActive?: boolean;
  testId?: string;
  target?: string;
  icon?: ReactNode;
  badge?: ReactNode;
  dropdown?: ReactNode;
}

const TopNavLink = ({
  label,
  href,
  isActive,
  testId,
  target,
  icon,
  badge,
  dropdown,
}: TopNavLinkProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isLabelClick = useRef(false);

  const handleBlurNewTabLink = () => {
    (document.activeElement as HTMLElement)?.blur();
  };

  const linkContent = (
    <>
      {icon}
      {label}
      {badge}
    </>
  );

  if (dropdown == null) {
    return (
      <Link
        href={href}
        className={clsx(classes.root, { [classes.active!]: isActive })}
        data-testid={testId}
        target={target}
        onClick={target ? handleBlurNewTabLink : undefined}
      >
        {linkContent}
      </Link>
    );
  }

  // Mantine toggles on a click anywhere in the target, which wraps the whole item so the
  // dropdown lines up with it. A click on the label follows the link and leaves the menu closed,
  // so only the caret, a button of its own, opens it, by mouse or keyboard. Mantine also puts
  // its popup roles on that plain wrapper, which assistive tech ignores, so the button carries
  // them as well.
  const handleMenuChange = (opened: boolean) => {
    setIsMenuOpen(opened && !isLabelClick.current);
    isLabelClick.current = false;
  };

  const handleLabelClick = () => {
    isLabelClick.current = true;
    if (target) {
      handleBlurNewTabLink();
    }
  };

  return (
    <Menu
      offset={5}
      onChange={handleMenuChange}
      opened={isMenuOpen}
      position="bottom-start"
      transitionProps={{ duration: 150, transition: 'scale-y', timingFunction: 'ease-out' }}
      width={280}
    >
      <Menu.Target>
        <Box className={clsx(classes.root, classes.withDropdown, { [classes.active!]: isActive })}>
          <Link
            href={href}
            className={classes.label}
            data-testid={testId}
            target={target}
            onClick={handleLabelClick}
          >
            {linkContent}
          </Link>
          <UnstyledButton
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            aria-label={`${label} menu`}
            className={classes.caret}
            type="button"
          >
            <FiChevronDown aria-hidden size={16} />
          </UnstyledButton>
        </Box>
      </Menu.Target>
      <Menu.Dropdown>{dropdown}</Menu.Dropdown>
    </Menu>
  );
};

export default TopNavLink;
